import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import DarkLayout from './DarkLayout';
import { ResponsiveToolbarButton } from './ResponsiveToolbarButton';
import { apiService } from '../services/api';
import {
  Estimate as EstimateType,
  EstimateItem,
  UpdateEstimateItem,
  CostItemDetail,
  CostLibraryItem,
  CostLibraryFilter,
  Category,
  CostItemSupplier,
  Project,
  EstimateActivity,
} from '../types/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
} from '@mui/material';
import { listPageDialogPaperProps } from '../theme/darkDialog';
import { formatCurrency } from '../utils/formatCurrency';
import DxfExtractionDetailsPanel from './DxfExtractionDetailsPanel';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions } from '../constants/permissions';
import { useDeleteConfirmation } from '../hooks/useDeleteConfirmation';
import {
  parseDxfExtractionDetailsJson,
  type DxfExtractionDetailsPayload,
} from '../types/dxfExtractionDetails';

const CATEGORY_ICONS: Record<string, string> = {
  'Foundation': 'layers',
  'Earthwork': 'layers',
  'Superstructure': 'home_work',
  'default': 'category',
};

function getCategoryIcon(categoryName: string): string {
  const key = Object.keys(CATEGORY_ICONS).find((k) =>
    categoryName.toLowerCase().includes(k.toLowerCase())
  );
  return key ? CATEGORY_ICONS[key] : CATEGORY_ICONS.default;
}

function OriginBadge({ creationEventType }: { creationEventType?: number }) {
  if (creationEventType === 1)
    return (
      <span className="inline-flex items-center rounded-full bg-sky-500/15 text-sky-300 text-[10px] font-bold px-2 py-0.5 uppercase tracking-wide">
        From DXF
      </span>
    );
  if (creationEventType === 2)
    return (
      <span className="inline-flex items-center rounded-full bg-violet-500/15 text-violet-300 text-[10px] font-bold px-2 py-0.5 uppercase tracking-wide">
        Manual
      </span>
    );
  return null;
}

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

function formatEffectiveDate(dateStr?: string): string {
  if (!dateStr) return '—';
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString();
}

function resolveLatestSupplierRows(suppliers: CostItemSupplier[], asOf = new Date()): CostItemSupplier[] {
  const asOfTime = asOf.getTime();
  const bySupplier = new Map<number, CostItemSupplier[]>();
  for (const supplier of suppliers) {
    const rows = bySupplier.get(supplier.supplierId) ?? [];
    rows.push(supplier);
    bySupplier.set(supplier.supplierId, rows);
  }

  const resolved: CostItemSupplier[] = [];
  for (const rows of Array.from(bySupplier.values())) {
    const sorted = [...rows].sort((a, b) => {
      const aTime = new Date(a.effectiveDate).getTime();
      const bTime = new Date(b.effectiveDate).getTime();
      const aPastOrCurrent = Number.isFinite(aTime) ? aTime <= asOfTime : false;
      const bPastOrCurrent = Number.isFinite(bTime) ? bTime <= asOfTime : false;
      if (aPastOrCurrent !== bPastOrCurrent) return aPastOrCurrent ? -1 : 1;
      return bTime - aTime;
    });
    if (sorted[0]) resolved.push(sorted[0]);
  }

  return resolved.sort((a, b) => a.supplierName.localeCompare(b.supplierName));
}

function normalizeCommittedQty(raw: string): number {
  const n = Number(raw);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.round(n));
}

function blockQtyNonIntegerKeys(e: React.KeyboardEvent<HTMLElement>) {
  if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
    e.preventDefault();
  }
}

const EstimateDetail: React.FC = () => {
  const { projectId, estimateId } = useParams<{ projectId: string; estimateId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirmDelete, deleteConfirmationDialog } = useDeleteConfirmation();
  const canCreateEstimate = hasPermission(user, permissions.estimates.create);
  const canEditEstimate = hasPermission(user, permissions.estimates.edit);
  const canDeleteEstimate = hasPermission(user, permissions.estimates.delete);
  const [project, setProject] = useState<Project | null>(null);
  const [estimate, setEstimate] = useState<EstimateType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [supplierPopupOpen, setSupplierPopupOpen] = useState(false);
  const [supplierPopupItem, setSupplierPopupItem] = useState<EstimateItem | null>(null);
  const [supplierPopupSuppliers, setSupplierPopupSuppliers] = useState<CostItemSupplier[]>([]);
  const [addFromLibraryOpen, setAddFromLibraryOpen] = useState(false);
  const [libraryItems, setLibraryItems] = useState<CostLibraryItem[]>([]);
  const [libraryCategories, setLibraryCategories] = useState<Category[]>([]);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryCategoryId, setLibraryCategoryId] = useState<number | null>(null);
  const [selectedCostItem, setSelectedCostItem] = useState<CostItemDetail | null>(null);
  const [addQuantity, setAddQuantity] = useState(1);
  const [addSupplierId, setAddSupplierId] = useState<number | null>(null);
  const [editEstimateDialogOpen, setEditEstimateDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContingencyPercent, setEditContingencyPercent] = useState(10);
  const [editTaxPercent, setEditTaxPercent] = useState(0);
  const [editStatus, setEditStatus] = useState('Draft');
  const [savingEstimate, setSavingEstimate] = useState(false);
  const [activity, setActivity] = useState<EstimateActivity[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [dxfExtractionDetails, setDxfExtractionDetails] = useState<{
    payload: DxfExtractionDetailsPayload;
    processedAt: string;
  } | null>(null);
  const [dxfExtractionParseError, setDxfExtractionParseError] = useState<string | null>(null);
  const [dxfExtractionRawFallback, setDxfExtractionRawFallback] = useState<string | null>(null);
  const [qtyDraftByItemId, setQtyDraftByItemId] = useState<Record<string, string>>({});

  const documentPageTitle = useMemo(() => {
    const t = estimate?.title?.trim();
    if (t) return t;
    if (estimateId) return `Estimate ${estimateId.slice(0, 8)}…`;
    return 'Estimate';
  }, [estimate?.title, estimateId]);

  useDocumentTitle(documentPageTitle);

  const loadProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const p = await apiService.getProject(projectId);
      setProject(p);
    } catch (err) {
      console.error(err);
    }
  }, [projectId]);

  const loadEstimate = useCallback(async () => {
    if (!estimateId) return;
    try {
      setLoading(true);
      setError(null);
      const est = await apiService.getEstimate(estimateId);
      setEstimate(est);
    } catch (err) {
      console.error(err);
      setError('Failed to load estimate');
    } finally {
      setLoading(false);
    }
  }, [estimateId]);

  useEffect(() => {
    setQtyDraftByItemId({});
  }, [estimateId]);

  const mergeEstimateItem = useCallback((updated: EstimateItem) => {
    setEstimate((prev) => {
      if (!prev) return prev;
      const items = (prev.items ?? []).map((it) =>
        it.estimateItemId === updated.estimateItemId ? updated : it
      );
      return { ...prev, items };
    });
  }, []);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  useEffect(() => {
    loadEstimate();
  }, [loadEstimate]);

  useEffect(() => {
    if (!logsOpen || !estimateId) return;
    let cancelled = false;
    setActivityLoading(true);
    apiService
      .getEstimateActivity(estimateId)
      .then((rows) => {
        if (!cancelled) setActivity(rows);
      })
      .catch(() => {
        if (!cancelled) setActivity([]);
      })
      .finally(() => {
        if (!cancelled) setActivityLoading(false);
      });
    return () => {
      cancelled = true;
      setActivityLoading(false);
    };
  }, [logsOpen, estimateId]);

  const handleUpdateItem = async (itemId: string, patch: UpdateEstimateItem) => {
    if (!estimateId) return;
    if (!canEditEstimate) return;
    try {
      const updated = await apiService.updateEstimateItem(estimateId, itemId, patch);
      mergeEstimateItem(updated);
      if (patch.quantity !== undefined) {
        setQtyDraftByItemId((prev) => {
          const next = { ...prev };
          delete next[itemId];
          return next;
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const commitQtyForItem = (item: EstimateItem, raw: string) => {
    const v = normalizeCommittedQty(raw);
    const current = Math.round(item.quantity);
    if (v === current) {
      setQtyDraftByItemId((prev) => {
        const next = { ...prev };
        delete next[item.estimateItemId];
        return next;
      });
      return;
    }
    void handleUpdateItem(item.estimateItemId, { quantity: v });
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!estimateId || !canDeleteEstimate) return;
    const confirmed = await confirmDelete({ message: 'Remove this line?' });
    if (!confirmed) return;
    try {
      await apiService.deleteEstimateItem(estimateId, itemId);
      loadEstimate();
    } catch (err) {
      console.error(err);
    }
  };

  const openSupplierPopup = async (item: EstimateItem) => {
    setSupplierPopupItem(item);
    setSupplierPopupSuppliers([]);
    setSupplierPopupOpen(true);
    try {
      const detail = await apiService.getCostItem(item.costItemId);
      const latestSuppliers = resolveLatestSupplierRows(detail.suppliers ?? []);
      const suppliersByPriceDesc = [...latestSuppliers].sort((a, b) => {
        if (b.unitCost !== a.unitCost) return b.unitCost - a.unitCost;
        return a.supplierName.localeCompare(b.supplierName);
      });
      setSupplierPopupSuppliers(suppliersByPriceDesc);
    } catch (err) {
      console.error(err);
    }
  };

  const chooseSupplier = async (costItemSupplierId: number) => {
    if (!supplierPopupItem || !estimateId) return;
    if (!canEditEstimate) return;
    try {
      const updated = await apiService.updateEstimateItem(estimateId, supplierPopupItem.estimateItemId, {
        selectedCostItemSupplierId: costItemSupplierId,
      });
      mergeEstimateItem(updated);
      setSupplierPopupOpen(false);
      setSupplierPopupItem(null);
    } catch (err) {
      console.error(err);
    }
  };

  const openAddFromLibrary = async () => {
    if (!canCreateEstimate) return;
    setAddFromLibraryOpen(true);
    setSelectedCostItem(null);
    setAddQuantity(1);
    setAddSupplierId(null);
    try {
      const [cats, result] = await Promise.all([
        apiService.getCategories(),
        apiService.getCostLibraryItems({ pageNumber: 1, pageSize: 50 }),
      ]);
      setLibraryCategories(cats);
      setLibraryItems(result.items);
    } catch (err) {
      console.error(err);
    }
  };

  const searchLibrary = async () => {
    try {
      const filter: CostLibraryFilter = {
        pageNumber: 1,
        pageSize: 50,
        searchTerm: librarySearch || undefined,
        categoryId: libraryCategoryId ?? undefined,
      };
      const result = await apiService.getCostLibraryItems(filter);
      setLibraryItems(result.items);
    } catch (err) {
      console.error(err);
    }
  };

  const selectLibraryItem = async (costItemId: number) => {
    try {
      const detail = await apiService.getCostItem(costItemId);
      const latestSuppliers = resolveLatestSupplierRows(detail.suppliers ?? []);
      const firstSupplier = latestSuppliers[0];
      setSelectedCostItem({ ...detail, suppliers: latestSuppliers });
      setAddSupplierId(firstSupplier ? firstSupplier.costItemSupplierId : null);
    } catch (err) {
      console.error(err);
    }
  };

  const addItemToEstimate = async () => {
    if (!estimateId || !selectedCostItem) return;
    if (!canCreateEstimate) return;
    const qty = Math.max(0, Math.round(addQuantity));
    try {
      const newItem = await apiService.addEstimateItem(estimateId, {
        costItemId: selectedCostItem.costItemId,
        quantity: qty,
        unitId: selectedCostItem.unitId,
        selectedCostItemSupplierId: addSupplierId ?? undefined,
      });
      setEstimate((prev) => {
        if (!prev) return prev;
        return { ...prev, items: [...(prev.items ?? []), newItem] };
      });
      setAddFromLibraryOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const totalAmount = estimate?.items?.reduce((sum, i) => sum + (i.lineTotal ?? 0), 0) ?? 0;
  const contingencyPercent = estimate?.contingencyPercent ?? 10;
  const contingencyAmount = totalAmount * (contingencyPercent / 100);
  const totalWithContingency = totalAmount + contingencyAmount;
  const taxPercent = estimate?.taxPercent ?? 0;
  const taxAmount = totalWithContingency * (taxPercent / 100);
  const totalWithTax = totalWithContingency + taxAmount;

  const openEditEstimateDialog = () => {
    if (!canEditEstimate) return;
    setEditTitle(estimate?.title ?? '');
    setEditContingencyPercent(estimate?.contingencyPercent ?? 10);
    setEditTaxPercent(estimate?.taxPercent ?? 0);
    setEditStatus(estimate?.status ?? 'Draft');
    setEditEstimateDialogOpen(true);
  };

  const handleSaveEstimateDetails = async () => {
    if (!estimateId) return;
    if (!canEditEstimate) return;
    setSavingEstimate(true);
    try {
      await apiService.updateEstimate(estimateId, {
        title: editTitle || undefined,
        contingencyPercent: editContingencyPercent,
        taxPercent: editTaxPercent,
        status: editStatus,
      });
      await loadEstimate();
      setEditEstimateDialogOpen(false);
    } catch (err) {
      console.error(err);
      setError('Failed to update estimate');
    } finally {
      setSavingEstimate(false);
    }
  };

  const groupedByCategory = useMemo(() => {
    const items = estimate?.items ?? [];
    const map = new Map<string, EstimateItem[]>();
    for (const item of items) {
      const cat = item.categoryName ?? 'Other';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(item);
    }
    return Array.from(map.entries());
  }, [estimate?.items]);

  const groupedForReview = (estimate?.items ?? []).reduce<
    Record<string, Record<string, EstimateItem[]>>
  >((acc, item) => {
    const cat = item.categoryName ?? 'Other';
    const group = item.costItemGroupName ?? 'Other';
    if (!acc[cat]) acc[cat] = {};
    if (!acc[cat][group]) acc[cat][group] = [];
    acc[cat][group].push(item);
    return acc;
  }, {} as Record<string, Record<string, EstimateItem[]>>);

  if (!projectId || !estimateId) return null;

  const headerRight = canEditEstimate ? (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={openEditEstimateDialog}
        aria-label="Edit estimate title, contingency, tax and status"
        className="flex items-center justify-center rounded-full size-10 bg-primary text-white shadow-lg shadow-primary/20 hover:opacity-90"
      >
        <span className="material-symbols-outlined text-xl">edit</span>
      </button>
    </div>
  ) : null;

  return (
    <DarkLayout
      title={project?.name ?? estimate?.title ?? 'Estimate'}
      subtitle={
        estimate?.title ??
        (estimateId ? `Estimate ${estimateId.replace(/-/g, '').slice(0, 8).toUpperCase()}` : 'Estimate')
      }
      headerRight={headerRight}
    >
      <div className="flex flex-col min-h-full p-4">
        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-red-400 text-sm mb-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="bg-elevation-dark rounded-xl p-6 border border-border-dark">
            <p className="text-center text-slate-400">Loading estimate...</p>
          </div>
        ) : (
          <>
            {/* Stats grid - template */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 p-4 border-b border-border-dark bg-background-dark/50 -mx-4 -mt-0 mb-4">
              <div className="flex flex-col gap-1">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                  Total Cost
                </p>
                <p className="text-white text-xl font-bold">{formatCurrency(totalWithTax)}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                  Contingency
                </p>
                <p className="text-emerald-400 text-xl font-bold">{contingencyPercent}%</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                  Tax
                </p>
                <p className="text-amber-400 text-xl font-bold">{taxPercent}%</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                  Status
                </p>
                <p className="text-accent-blue text-xl font-bold">{estimate?.status ?? 'Draft'}</p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                  Last Edited
                </p>
                <p className="text-slate-300 text-xl font-bold">
                  {formatRelativeTime(estimate?.updatedAt ?? estimate?.createdAt)}
                </p>
              </div>
            </div>

            {estimate && (estimate.dxfLinearUnitScaleToMeter ?? 1) !== 1 && (
              <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-amber-200 text-sm">
                Quantities scaled from drawing units: 1 drawing unit = {estimate.dxfLinearUnitScaleToMeter} m
              </div>
            )}

            {/* Action bar - template */}
            <div className="flex flex-wrap gap-2 items-center justify-between mb-6">
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 lg:pb-0">
                <ResponsiveToolbarButton
                  icon="arrow_back"
                  label="Back"
                  variant="primary"
                  onClick={() => projectId && navigate(`/projects/${projectId}/estimates`)}
                  mobileAriaLabel="Back to project estimates"
                />
                <ResponsiveToolbarButton
                  icon="history"
                  label="Review"
                  onClick={() => setReviewOpen(true)}
                  mobileAriaLabel="Review estimate"
                />
                <ResponsiveToolbarButton
                  icon="list_alt"
                  label="Logs"
                  onClick={() => {
                    setDxfExtractionDetails(null);
                    setDxfExtractionParseError(null);
                    setDxfExtractionRawFallback(null);
                    setActivityLoading(true);
                    setLogsOpen(true);
                  }}
                  mobileAriaLabel="View extraction logs"
                />
              </div>
            </div>

            {/* Category sections - template */}
            <div className="space-y-6 pb-12">
              {groupedByCategory.map(([categoryName, categoryItems], sectionIndex) => {
                const sectionTotal = categoryItems.reduce((s, i) => s + (i.lineTotal ?? 0), 0);
                const icon = getCategoryIcon(categoryName);
                return (
                  <div
                    key={categoryName}
                    className="bg-elevation-dark rounded-xl border border-border-dark shadow-xl overflow-hidden"
                  >
                    <div className="p-4 bg-slate-800/40 border-b border-border-dark flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-slate-400">{icon}</span>
                        <h3 className="text-white font-bold text-base tracking-wide uppercase">
                          {sectionIndex + 1}.0 {categoryName}
                        </h3>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-slate-200 font-bold text-sm">
                          {formatCurrency(sectionTotal)}
                        </span>
                        <button
                          type="button"
                          className="text-slate-500 hover:text-white"
                          aria-label="More options"
                        >
                          <span className="material-symbols-outlined">more_vert</span>
                        </button>
                      </div>
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold border-b border-border-dark">
                            <th className="px-6 py-4 font-bold">Item Description</th>
                            <th className="px-6 py-4 text-center font-bold">Qty</th>
                            <th className="px-6 py-4 text-center font-bold">Unit</th>
                            <th className="px-6 py-4 text-right font-bold">Unit Price</th>
                            <th className="px-6 py-4 text-right font-bold">Total</th>
                            <th className="px-6 py-4 w-10"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-dark/30">
                          {categoryItems.map((item) => (
                            <tr
                              key={item.estimateItemId}
                              className="hover:bg-white/5 transition-colors group"
                            >
                              <td className="px-6 py-5">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-white font-medium text-sm">{item.itemName}</p>
                                  <OriginBadge creationEventType={item.creationEventType} />
                                </div>
                                <p className="text-slate-500 text-xs mt-0.5">
                                  {[item.categoryName, item.costItemGroupName]
                                    .filter(Boolean)
                                    .join(' / ') || '—'}
                                  {item.notes ? ` · ${item.notes}` : ''}
                                </p>
                              </td>
                              <td className="px-6 py-5 text-center">
                                <input
                                  type="number"
                                  min={0}
                                  step={1}
                                  inputMode="numeric"
                                  className="w-20 rounded border border-border-dark bg-elevation-dark px-2 py-1 text-sm text-slate-300 text-center focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                  value={
                                    qtyDraftByItemId[item.estimateItemId] !== undefined
                                      ? qtyDraftByItemId[item.estimateItemId]
                                      : String(Math.round(item.quantity))
                                  }
                                  onChange={(e) =>
                                    setQtyDraftByItemId((prev) => ({
                                      ...prev,
                                      [item.estimateItemId]: e.target.value,
                                    }))
                                  }
                                  onKeyDown={(e) => {
                                    blockQtyNonIntegerKeys(e);
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      commitQtyForItem(item, e.currentTarget.value);
                                    }
                                  }}
                                  onBlur={(e) => commitQtyForItem(item, e.target.value)}
                                />
                              </td>
                              <td className="px-6 py-5 text-center text-slate-500 text-xs font-bold uppercase">
                                {item.unitSymbol || item.unitName}
                              </td>
                              <td className="px-6 py-5 text-right text-slate-300 text-sm">
                                {formatCurrency(item.unitPrice)}
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                  Eff: {formatEffectiveDate(item.resolvedEffectiveDate)}
                                </div>
                              </td>
                              <td className="px-6 py-5 text-right text-white font-semibold text-sm">
                                {formatCurrency(item.lineTotal)}
                              </td>
                              <td className="px-6 py-5">
                                <span className="inline-flex items-center gap-0.5">
                                  {item.isCheapestSupplier && (
                                    <span
                                      className="material-symbols-outlined text-green-500 text-lg"
                                      title="Cheapest supplier"
                                    >
                                      check_circle
                                    </span>
                                  )}
                                  {canEditEstimate && (
                                    <button
                                      type="button"
                                      onClick={() => openSupplierPopup(item)}
                                      className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-primary"
                                      title="Select supplier"
                                    >
                                      <span className="material-symbols-outlined text-lg">store</span>
                                    </button>
                                  )}
                                  {canDeleteEstimate && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteItem(item.estimateItemId)}
                                      className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-red-400"
                                      aria-label="Delete"
                                    >
                                      <span className="material-symbols-outlined text-lg">delete</span>
                                    </button>
                                  )}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden divide-y divide-border-dark/30">
                      {categoryItems.map((item) => (
                        <div key={item.estimateItemId} className="p-4 space-y-4">
                          <div className="flex justify-between items-start">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-white font-bold text-sm">{item.itemName}</p>
                                <OriginBadge creationEventType={item.creationEventType} />
                              </div>
                              <p className="text-slate-500 text-[10px] mt-0.5 line-clamp-1">
                                {[item.categoryName, item.costItemGroupName]
                                  .filter(Boolean)
                                  .join(' / ') || '—'}
                              </p>
                            </div>
                            <span className="inline-flex items-center gap-1">
                              {item.isCheapestSupplier && (
                                <span
                                  className="material-symbols-outlined text-green-500 text-lg"
                                  title="Cheapest"
                                >
                                  check_circle
                                </span>
                              )}
                              {canEditEstimate && (
                                <button
                                  type="button"
                                  onClick={() => openSupplierPopup(item)}
                                  className="text-slate-500 hover:text-primary p-1"
                                  aria-label="Supplier"
                                >
                                  <span className="material-symbols-outlined text-lg">store</span>
                                </button>
                              )}
                              {canDeleteEstimate && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteItem(item.estimateItemId)}
                                  className="text-slate-500 hover:text-red-400 p-1"
                                  aria-label="Delete"
                                >
                                  <span className="material-symbols-outlined text-lg">delete</span>
                                </button>
                              )}
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div className="flex flex-col">
                              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                                Qty
                              </span>
                              <input
                                type="number"
                                min={0}
                                step={1}
                                inputMode="numeric"
                                className="mt-0.5 rounded border border-border-dark bg-elevation-dark px-2 py-1 text-xs text-slate-200"
                                value={
                                  qtyDraftByItemId[item.estimateItemId] !== undefined
                                    ? qtyDraftByItemId[item.estimateItemId]
                                    : String(Math.round(item.quantity))
                                }
                                onChange={(e) =>
                                  setQtyDraftByItemId((prev) => ({
                                    ...prev,
                                    [item.estimateItemId]: e.target.value,
                                  }))
                                }
                                onKeyDown={(e) => {
                                  blockQtyNonIntegerKeys(e);
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    commitQtyForItem(item, e.currentTarget.value);
                                  }
                                }}
                                onBlur={(e) => commitQtyForItem(item, e.target.value)}
                              />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                                Unit
                              </span>
                              <span className="text-slate-200 text-xs font-medium mt-0.5">
                                {item.unitSymbol || item.unitName}
                              </span>
                            </div>
                            <div className="flex flex-col text-right">
                              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                                Price
                              </span>
                              <span className="text-slate-200 text-xs font-medium mt-0.5">
                                {formatCurrency(item.unitPrice)}
                              </span>
                              <span className="text-slate-500 text-[10px] mt-0.5">
                                Eff: {formatEffectiveDate(item.resolvedEffectiveDate)}
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t border-border-dark/20">
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                              Total
                            </span>
                            <span className="text-white font-bold">
                              {formatCurrency(item.lineTotal)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="p-4 border-t border-border-dark flex justify-start">
                      {canCreateEstimate && (
                        <button
                          type="button"
                          onClick={openAddFromLibrary}
                          className="text-primary text-sm font-bold flex items-center gap-1 hover:underline"
                        >
                          <span className="material-symbols-outlined text-base">add</span> Add Line
                          Item
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {groupedByCategory.length === 0 && (
                <div className="bg-elevation-dark rounded-xl border border-border-dark p-8 text-center">
                  <p className="text-slate-400 mb-4">No line items yet.</p>
                  {canCreateEstimate && (
                    <button
                      type="button"
                      onClick={openAddFromLibrary}
                      className="text-primary text-sm font-bold flex items-center gap-1 hover:underline mx-auto"
                    >
                      <span className="material-symbols-outlined text-base">add</span> Add from Cost
                      Library
                    </button>
                  )}
                </div>
              )}

              {/* Summary - template */}
              <div className="mt-8 bg-slate-900/50 rounded-xl border border-border-dark p-6">
                <div className="space-y-3 max-w-md ml-auto">
                  <div className="flex justify-between items-center text-slate-400">
                    <span className="text-sm">Subtotal Items</span>
                    <span className="font-medium">{formatCurrency(totalAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center text-slate-400">
                    <span className="text-sm">Contingency ({contingencyPercent}%)</span>
                    <span className="font-medium">{formatCurrency(contingencyAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center text-slate-400">
                    <span className="text-sm">Tax ({taxPercent}%)</span>
                    <span className="font-medium">{formatCurrency(taxAmount)}</span>
                  </div>
                  <div className="h-px bg-border-dark my-2" />
                  <div className="flex flex-col items-end sm:flex-row sm:justify-between sm:items-center text-white gap-1 sm:gap-0">
                    <span className="text-base sm:text-lg font-bold">Total Project Estimate</span>
                    <span className="text-xl sm:text-2xl font-black text-accent-blue">
                      {formatCurrency(totalWithTax)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Review estimate dialog */}
      <Dialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={listPageDialogPaperProps}
      >
        <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Review estimate
        </DialogTitle>
        <DialogContent>
          {Object.entries(groupedForReview).map(([categoryName, groups]) => (
            <Accordion
              key={categoryName}
              defaultExpanded
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'none',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <span className="material-symbols-outlined text-white">expand_more</span>
                }
              >
                <span className="font-bold text-white">{categoryName}</span>
              </AccordionSummary>
              <AccordionDetails>
                {Object.entries(groups).map(([groupName, groupItems]) => {
                  const groupTotal = groupItems.reduce((s, i) => s + i.lineTotal, 0);
                  return (
                    <Accordion
                      key={groupName}
                      sx={{
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <span className="material-symbols-outlined text-white">expand_more</span>
                        }
                      >
                        <span className="text-white">{groupName}</span>
                        <span className="ml-2 text-gray-400">{formatCurrency(groupTotal)}</span>
                      </AccordionSummary>
                      <AccordionDetails>
                        <table className="w-full text-sm text-left">
                          <thead>
                            <tr>
                              <th className="pr-2 py-1 text-gray-400 font-medium">Item</th>
                              <th className="pr-2 py-1 text-gray-400 font-medium text-right">
                                Qty
                              </th>
                              <th className="pr-2 py-1 text-gray-400 font-medium">Unit</th>
                              <th className="pr-2 py-1 text-gray-400 font-medium text-right">
                                Unit price
                              </th>
                              <th className="py-1 text-gray-400 font-medium text-right">
                                Line total
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupItems.map((i) => (
                              <tr key={i.estimateItemId}>
                                <td className="pr-2 py-1 text-white">
                                  {i.itemName}
                                  {i.isCheapestSupplier && (
                                    <span className="material-symbols-outlined text-green-500 ml-1 align-middle text-base">
                                      check_circle
                                    </span>
                                  )}
                                </td>
                                <td className="pr-2 py-1 text-white text-right">{i.quantity}</td>
                                <td className="pr-2 py-1 text-white">
                                  {i.unitSymbol || i.unitName}
                                </td>
                                <td className="pr-2 py-1 text-white text-right">
                                  {formatCurrency(i.unitPrice)}
                                </td>
                                <td className="py-1 text-white text-right">
                                  {formatCurrency(i.lineTotal)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          ))}
          <p className="mt-4 text-lg font-semibold text-white">
            Total: {formatCurrency(totalAmount)}
          </p>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            onClick={() => setReviewOpen(false)}
            className="rounded-lg border border-gray-600 bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-gray-600"
          >
            Close
          </button>
        </DialogActions>
      </Dialog>

      {/* Supplier selection dialog */}
      <Dialog
        open={supplierPopupOpen}
        onClose={() => {
          setSupplierPopupOpen(false);
          setSupplierPopupItem(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={listPageDialogPaperProps}
      >
        <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Select supplier / price for {supplierPopupItem?.itemName}
        </DialogTitle>
        <DialogContent>
          <table className="w-full text-sm text-left">
            <thead>
              <tr>
                <th className="pr-2 py-2 text-gray-400 font-medium">Supplier</th>
                <th className="pr-2 py-2 text-gray-400 font-medium text-right">Unit price</th>
                <th className="pr-2 py-2 text-gray-400 font-medium">Effective date</th>
                <th className="pr-2 py-2 text-gray-400 font-medium">Lead time</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {supplierPopupSuppliers.map((s) => (
                <tr key={s.costItemSupplierId} className="border-t border-border-dark">
                  <td className="pr-2 py-2 text-white">{s.supplierName}</td>
                  <td className="pr-2 py-2 text-white text-right">
                    {formatCurrency(s.unitCost)}
                  </td>
                  <td className="pr-2 py-2 text-white">{formatEffectiveDate(s.effectiveDate)}</td>
                  <td className="pr-2 py-2 text-white">
                    {s.leadTimeDays != null ? `${s.leadTimeDays} days` : '—'}
                  </td>
                  <td className="py-2">
                    <button
                      type="button"
                      onClick={() => chooseSupplier(s.costItemSupplierId)}
                      className="rounded-lg bg-primary px-3 py-1.5 text-sm font-bold text-white hover:bg-primary/90"
                    >
                      Use this supplier
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            onClick={() => {
              setSupplierPopupOpen(false);
              setSupplierPopupItem(null);
            }}
            className="rounded-lg border border-gray-600 bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-gray-600"
          >
            Cancel
          </button>
        </DialogActions>
      </Dialog>

      {/* Add from Cost Library dialog */}
      <Dialog
        open={addFromLibraryOpen}
        onClose={() => setAddFromLibraryOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={listPageDialogPaperProps}
      >
        <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Add from Cost Library
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <TextField
              size="small"
              placeholder="Search"
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchLibrary()}
              sx={{ flex: 1, minWidth: 120 }}
            />
            <TextField
              size="small"
              select
              label="Category"
              value={libraryCategoryId ?? ''}
              onChange={(e) =>
                setLibraryCategoryId(e.target.value ? Number(e.target.value) : null)
              }
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              {libraryCategories.map((c) => (
                <MenuItem key={c.categoryId} value={c.categoryId}>
                  {c.categoryName}
                </MenuItem>
              ))}
            </TextField>
            <button
              type="button"
              onClick={searchLibrary}
              className="flex h-10 items-center justify-center rounded-lg border border-gray-600 bg-gray-700 px-4 text-sm font-medium text-gray-200 hover:bg-gray-600"
            >
              Apply
            </button>
          </div>
          {!selectedCostItem ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr>
                    <th className="pr-2 py-2 text-gray-400 font-medium">Code</th>
                    <th className="pr-2 py-2 text-gray-400 font-medium">Name</th>
                    <th className="pr-2 py-2 text-gray-400 font-medium">Category</th>
                    <th className="pr-2 py-2 text-gray-400 font-medium text-right">Price</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {libraryItems.map((li) => (
                    <tr key={li.costItemId} className="border-t border-border-dark">
                      <td className="pr-2 py-2 text-white">{li.itemCode}</td>
                      <td className="pr-2 py-2 text-white">{li.itemName}</td>
                      <td className="pr-2 py-2 text-white">{li.categoryName}</td>
                      <td className="pr-2 py-2 text-white text-right">
                        {formatCurrency(li.lowestPrice)}
                      </td>
                      <td className="py-2">
                        <button
                          type="button"
                          onClick={() => selectLibraryItem(li.costItemId)}
                          className="rounded-lg bg-primary px-3 py-1.5 text-sm font-bold text-white hover:bg-primary/90"
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-white font-medium">{selectedCostItem.itemName}</p>
              <TextField
                margin="dense"
                label="Quantity"
                type="number"
                inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
                value={addQuantity}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    setAddQuantity(0);
                    return;
                  }
                  const n = Number(raw);
                  if (Number.isNaN(n)) return;
                  setAddQuantity(Math.max(0, Math.round(n)));
                }}
                onKeyDown={(e) => blockQtyNonIntegerKeys(e)}
              />
              <TextField
                margin="dense"
                select
                label="Supplier"
                fullWidth
                value={addSupplierId ?? ''}
                onChange={(e) =>
                  setAddSupplierId(e.target.value ? Number(e.target.value) : null)
                }
              >
                {(selectedCostItem.suppliers ?? []).map((s) => (
                  <MenuItem key={s.costItemSupplierId} value={s.costItemSupplierId}>
                    {s.supplierName} — {formatCurrency(s.unitCost)} (eff {formatEffectiveDate(s.effectiveDate)})
                  </MenuItem>
                ))}
              </TextField>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCostItem(null)}
                  className="rounded-lg border border-gray-600 bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-gray-600"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={addItemToEstimate}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-primary/90 disabled:opacity-50"
                  disabled={!canCreateEstimate}
                >
                  Add to estimate
                </button>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            onClick={() => setAddFromLibraryOpen(false)}
            className="rounded-lg border border-gray-600 bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-gray-600"
          >
            Close
          </button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={logsOpen}
        onClose={() => {
          setLogsOpen(false);
          setDxfExtractionDetails(null);
          setDxfExtractionParseError(null);
          setDxfExtractionRawFallback(null);
        }}
        PaperProps={listPageDialogPaperProps}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          {dxfExtractionDetails || dxfExtractionParseError
            ? 'DXF extraction details'
            : 'Activity log'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {dxfExtractionDetails ? (
            <div className="pt-2 max-h-[min(70vh,720px)] overflow-y-auto pr-1">
              <button
                type="button"
                onClick={() => {
                  setDxfExtractionDetails(null);
                  setDxfExtractionParseError(null);
                  setDxfExtractionRawFallback(null);
                }}
                className="mb-3 text-sm font-medium text-primary hover:underline"
              >
                ← Back to log
              </button>
              <DxfExtractionDetailsPanel
                payload={dxfExtractionDetails.payload}
                processedAt={dxfExtractionDetails.processedAt}
              />
            </div>
          ) : dxfExtractionParseError ? (
            <div className="pt-2 max-h-[min(70vh,720px)] overflow-y-auto">
              <button
                type="button"
                onClick={() => {
                  setDxfExtractionParseError(null);
                  setDxfExtractionRawFallback(null);
                }}
                className="mb-3 text-sm font-medium text-primary hover:underline"
              >
                ← Back to log
              </button>
              <p className="text-amber-300 text-sm mb-3">{dxfExtractionParseError}</p>
              {dxfExtractionRawFallback && (
                <pre className="text-xs text-slate-300 whitespace-pre-wrap overflow-auto max-h-[min(50vh,360px)] bg-slate-900/80 p-3 rounded-lg border border-border-dark font-mono">
                  {dxfExtractionRawFallback}
                </pre>
              )}
            </div>
          ) : activityLoading ? (
            <div className="flex justify-center items-center py-12">
              <span className="material-symbols-outlined animate-spin text-4xl text-primary">
                progress_activity
              </span>
            </div>
          ) : activity.length === 0 ? (
            <p className="text-slate-500 text-sm py-2">No activity recorded yet.</p>
          ) : (
            <ul className="space-y-3 max-h-[min(70vh,480px)] overflow-y-auto pt-2">
              {activity.map((a) => (
                <li key={a.estimateActivityId} className="border-b border-border-dark/40 pb-3 last:border-0">
                  <p className="text-slate-300 text-sm">{a.message}</p>
                  <p className="text-[10px] text-slate-500 mt-1">{new Date(a.createdAt).toLocaleString()}</p>
                  {a.eventType === 8 && a.detailsJson && (
                    <button
                      type="button"
                      className="mt-2 text-xs font-semibold text-primary hover:underline"
                      onClick={() => {
                        setDxfExtractionParseError(null);
                        setDxfExtractionRawFallback(null);
                        try {
                          const payload = parseDxfExtractionDetailsJson(a.detailsJson!);
                          setDxfExtractionDetails({ payload, processedAt: a.createdAt });
                        } catch {
                          setDxfExtractionDetails(null);
                          setDxfExtractionParseError(
                            'Could not read extraction details. Raw payload is shown below.'
                          );
                          setDxfExtractionRawFallback(a.detailsJson!);
                        }
                      }}
                    >
                      View extraction details
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <button
            type="button"
            onClick={() => {
              setLogsOpen(false);
              setDxfExtractionDetails(null);
              setDxfExtractionParseError(null);
              setDxfExtractionRawFallback(null);
            }}
            className="rounded-lg border border-border-dark bg-elevation-dark px-4 py-2 text-sm font-medium text-slate-300 hover:bg-white/5"
          >
            Close
          </button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editEstimateDialogOpen}
        onClose={() => !savingEstimate && setEditEstimateDialogOpen(false)}
        PaperProps={listPageDialogPaperProps}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Edit estimate
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <div className="flex flex-col gap-4 pt-5">
            <TextField
              label="Estimate title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g. Phase 1 – Foundation"
            />
            <TextField
              label="Contingency (%)"
              type="number"
              value={editContingencyPercent}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v) && v >= 0 && v <= 100) setEditContingencyPercent(v);
              }}
              inputProps={{ min: 0, max: 100, step: 0.5 }}
              fullWidth
              size="small"
            />
            <TextField
              label="Tax (%)"
              type="number"
              value={editTaxPercent}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v) && v >= 0 && v <= 100) setEditTaxPercent(v);
              }}
              inputProps={{ min: 0, max: 100, step: 0.5 }}
              fullWidth
              size="small"
            />
            <TextField
              select
              label="Status"
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="Draft">Draft</MenuItem>
              <MenuItem value="Submitted">Submitted</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
              <MenuItem value="On Hold">On Hold</MenuItem>
            </TextField>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <button
            type="button"
            onClick={() => setEditEstimateDialogOpen(false)}
            disabled={savingEstimate}
            className="rounded-lg border border-border-dark bg-elevation-dark px-4 py-2 text-sm font-medium text-slate-300 hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveEstimateDetails}
            disabled={savingEstimate}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"
          >
            {savingEstimate ? 'Saving...' : 'Save'}
          </button>
        </DialogActions>
      </Dialog>
      {deleteConfirmationDialog}
    </DarkLayout>
  );
};

export default EstimateDetail;
